/**
 * Передача файлов между двумя браузерами.
 *
 * Файл режется на куски и уходит по каналу данных WebRTC напрямую второму
 * участнику. Сервер участвует только в знакомстве сторон и содержимого не
 * видит.
 *
 * Три решения, которые стоит объяснить.
 *
 * 1. Докачка. Получатель подтверждает принятые куски, отправитель помнит
 *    подтверждённое смещение. Обрыв соединения не начинает файл заново:
 *    на плохой мобильной сети это означало бы, что большой файл не уедет
 *    никогда.
 *
 * 2. Своё шифрование поверх WebRTC. Сам канал уже шифрован (DTLS), но
 *    ключи проверяются через отпечатки, которые идут через наш сигнальный
 *    сервер. Кто контролирует сигналинг, тот может подменить отпечатки и
 *    встать посередине. Секрет из фрагмента ссылки на сервер не попадает
 *    никогда, поэтому расшифровать куски может только тот, кому ссылку
 *    дали.
 *
 * 3. Поток управляется по `bufferedAmount`. Без этого отправитель забивает
 *    буфер гигабайтами быстрее, чем сеть их вывозит, и вкладка падает по
 *    памяти на первом же большом файле.
 */

/** Размер куска. Компромисс между накладными расходами и отзывчивостью. */
const CHUNK_SIZE = 64 * 1024;

/** Выше этого порога отправка приостанавливается, пока буфер не разгрузится. */
const BUFFER_HIGH = 8 * 1024 * 1024;
const BUFFER_LOW = 1 * 1024 * 1024;

/** Размер вектора инициализации AES-GCM. */
const IV_BYTES = 12;

/** Описание файла, которым стороны обмениваются до передачи. */
export interface FileMeta {
  id: string;
  name: string;
  size: number;
  type: string;
}

/** Сообщения канала. Двоичные куски идут отдельно, это только управление. */
export type ControlMessage =
  /** Отправитель объявляет состав передачи. */
  | { kind: 'offer-files'; files: FileMeta[] }
  /** Получатель просит файл, начиная с указанного смещения (докачка). */
  | { kind: 'request'; fileId: string; offset: number }
  /** Отправитель предупреждает, какой файл пойдёт следующим. */
  | { kind: 'begin'; fileId: string; offset: number }
  /** Получатель подтверждает принятое смещение. */
  | { kind: 'ack'; fileId: string; received: number }
  /** Файл передан целиком. */
  | { kind: 'done'; fileId: string }
  /** Передача прервана с объяснением. */
  | { kind: 'error'; fileId: string; message: string };

/**
 * Ключ шифрования из секрета ссылки.
 *
 * Секрет живёт во фрагменте адреса, поэтому на сервер он не уходит: браузер
 * фрагмент не отправляет. Соль постоянная и не секретна: она защищает от
 * заранее посчитанных таблиц, а сам секрет и так случайный.
 */
export async function keyFromSecret(secret: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new TextEncoder().encode('datahunter-file-transfer'),
      iterations: 100_000,
      hash: 'SHA-256',
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Случайный секрет для ссылки. */
export function makeSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  // base64url: значение попадает в адресную строку, а `+`, `/` и `=` там
  // требуют экранирования и путают при копировании.
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Шифрует кусок.
 *
 * Вектор инициализации кладётся в начало результата: он не секретен, но
 * обязан быть разным для каждого куска, иначе шифрование AES-GCM
 * компрометируется.
 */
export async function encryptChunk(key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);

  const out = new Uint8Array(IV_BYTES + cipher.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(cipher), IV_BYTES);

  return out.buffer;
}

/** Расшифровывает кусок. Битый или чужой кусок бросает исключение. */
export async function decryptChunk(key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer> {
  const iv = new Uint8Array(data, 0, IV_BYTES);
  const cipher = new Uint8Array(data, IV_BYTES);

  return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
}

/** Ждёт, пока буфер канала разгрузится: защита от переполнения памяти. */
async function drain(channel: RTCDataChannel): Promise<void> {
  if (channel.bufferedAmount < BUFFER_HIGH) return;

  channel.bufferedAmountLowThreshold = BUFFER_LOW;

  await new Promise<void>((resolve) => {
    const onLow = () => {
      channel.removeEventListener('bufferedamountlow', onLow);
      resolve();
    };
    channel.addEventListener('bufferedamountlow', onLow);
  });
}

/**
 * Отправляет файл, начиная с указанного смещения.
 *
 * Смещение не ноль, когда идёт докачка после обрыва. Возвращает смещение,
 * на котором остановились: при разрыве это точка продолжения.
 */
export async function sendFile({
  channel,
  key,
  file,
  meta,
  offset,
  onProgress,
  isOpen,
}: {
  channel: RTCDataChannel;
  key: CryptoKey;
  file: Blob;
  meta: FileMeta;
  offset: number;
  onProgress: (sent: number) => void;
  /** Проверка живости канала: при обрыве отправку надо прекратить сразу. */
  isOpen: () => boolean;
}): Promise<number> {
  let sent = offset;

  const begin: ControlMessage = { kind: 'begin', fileId: meta.id, offset };
  channel.send(JSON.stringify(begin));

  while (sent < file.size) {
    if (!isOpen()) return sent;

    const slice = file.slice(sent, Math.min(sent + CHUNK_SIZE, file.size));
    const plain = await slice.arrayBuffer();
    const cipher = await encryptChunk(key, plain);

    await drain(channel);
    if (!isOpen()) return sent;

    channel.send(cipher);
    sent += plain.byteLength;
    onProgress(sent);
  }

  const done: ControlMessage = { kind: 'done', fileId: meta.id };
  channel.send(JSON.stringify(done));

  return sent;
}

/**
 * Собирает принятые куски в файл.
 *
 * Куски накапливаются в памяти вкладки. Это ограничивает размер файла
 * доступной памятью, зато не требует разрешений на запись на диск и
 * работает одинаково во всех браузерах. Для файлов в сотни мегабайт этого
 * достаточно, а для больших нужен File System Access API, который есть не
 * везде.
 */
export class FileAssembler {
  private readonly parts: ArrayBuffer[] = [];
  private received = 0;

  constructor(readonly meta: FileMeta) {}

  /** Сколько байт уже принято: это же значение уходит в подтверждении. */
  get bytes(): number {
    return this.received;
  }

  add(chunk: ArrayBuffer): void {
    this.parts.push(chunk);
    this.received += chunk.byteLength;
  }

  get complete(): boolean {
    return this.received >= this.meta.size;
  }

  blob(): Blob {
    return new Blob(this.parts, { type: this.meta.type || 'application/octet-stream' });
  }
}

export { CHUNK_SIZE };
